import { useCallback, useEffect, useMemo, useRef } from 'react';

type DriverInstance = any;

export type DriverStep = {
  element: string;
  popover: {
    title?: string;
    description: string;
    side?: 'top' | 'bottom' | 'left' | 'right';
    align?: 'start' | 'center' | 'end';
  };
  padding?: number;
};

export type UseDriverTourOptions = {
  overlayOpacity?: number;
  allowClose?: boolean;
  showProgress?: boolean;
  stagePadding?: number;
  waitForElementsMs?: number;

  nextBtnText?: string; // i18n NEXT
  firstStepNextText?: string; // i18n START
  finishBtnText?: string; // i18n FINISH_TUTORIAL
  prevBtnText?: string; // i18n BACK
  doneBtnText?: string; // keep native "X" (unused)

  onDestroyed?: () => void;
  onCompleted?: () => void;
};

/** Selector exists in DOM? */
const exists = (sel: string) => {
  if (typeof document === 'undefined') return false;
  try {
    return !!document.querySelector(sel);
  } catch {
    return false;
  }
};

const waitUpTo = (selectors: string[], ms: number) =>
  new Promise<boolean>(resolve => {
    const start = performance.now();
    const tick = () => {
      const missing = selectors.filter(s => !exists(s));
      if (missing.length === 0) return resolve(true);
      if (performance.now() - start > ms) return resolve(false);
      requestAnimationFrame(tick);
    };
    tick();
  });

async function createDriver(options: any): Promise<DriverInstance | null> {
  const mod: any = await import('driver.js');
  if (typeof mod?.driver === 'function') return mod.driver(options);
  if (typeof mod?.default === 'function') return new mod.default(options);
  return null;
}

/** Parse "X of Y" or "X/Y"; fallback to attributes */
function parseProgress(popover: HTMLElement | null): { cur: number; total: number } {
  const text = popover?.textContent || '';
  const m = text.match(/(\d+)\s*(?:\/|of)\s*(\d+)/i);
  if (m) {
    const cur = parseInt(m[1], 10);
    const total = parseInt(m[2], 10);
    if (!Number.isNaN(cur) && !Number.isNaN(total) && total > 0) return { cur, total };
  }
  const curAttr = popover?.getAttribute('data-step-index');
  const totalAttr = popover?.getAttribute('data-steps-total');
  const cur = curAttr ? parseInt(curAttr, 10) + 1 : 1;
  const total = totalAttr ? parseInt(totalAttr, 10) : Math.max(cur, 1);
  return { cur, total };
}

function getCurrentStepIndex(popover: HTMLElement | null): number {
  const { cur } = parseProgress(popover);
  return Math.max(0, cur - 1);
}

/** Apply labels/state; keep native close icon; drive CSS attrs (no flicker) */
function applyNavState(popover: HTMLElement | null, opts?: UseDriverTourOptions) {
  if (!popover) return;

  // FIX: pre-hide nav until we finish stabilizing this step (kills per-step flicker)
  popover.setAttribute('data-nav-ready', '1');

  const { cur, total } = parseProgress(popover);
  const idx0 = Math.max(0, cur - 1);

  // Attributes consumed by CSS
  popover.setAttribute('data-step-index', String(idx0));
  popover.setAttribute('data-steps-total', String(total));

  const nav = popover.querySelector('.driver-popover-navigation-btns') as HTMLElement | null;
  const nextBtn = popover.querySelector('.driver-popover-next-btn') as HTMLButtonElement | null;
  const prevBtn = popover.querySelector('.driver-popover-prev-btn') as HTMLButtonElement | null;
  const closeIcon = popover.querySelector('.driver-close-btn-icon') as HTMLElement | null;
  const progressEl = popover.querySelector('.driver-popover-progress-text') as HTMLElement | null;

  // FIX: feed progress to CSS — we render "X/Y" via ::after (no text swaps => no flicker)
  if (progressEl) {
    progressEl.setAttribute('data-progress-current', String(cur));
    progressEl.setAttribute('data-progress-total', String(total));
  }

  // FIX: hard-apply fresh i18n labels every time (prevents stale "Next")
  if (nextBtn) {
    const first = idx0 === 0;
    const last = idx0 === total - 1;
    const label = first
      ? opts?.firstStepNextText || opts?.nextBtnText
      : last
      ? opts?.finishBtnText || opts?.nextBtnText
      : opts?.nextBtnText;
    if (label) {
      nextBtn.textContent = label;
      nextBtn.setAttribute('aria-label', label);
      nextBtn.setAttribute('title', label);
    }
  }
  if (prevBtn) {
    const plabel = opts?.prevBtnText;
    if (plabel) {
      prevBtn.textContent = plabel;
      prevBtn.setAttribute('aria-label', plabel);
      prevBtn.setAttribute('title', plabel);
    }
    if (idx0 === 0) {
      prevBtn.setAttribute('disabled', 'true');
      prevBtn.style.pointerEvents = 'none';
      prevBtn.style.opacity = '0.5';
    } else {
      prevBtn.removeAttribute('disabled');
      prevBtn.style.pointerEvents = '';
      prevBtn.style.opacity = '';
    }
  }

  // Step 0 layout: only centered GetStarted
  if (idx0 === 0) {
    if (closeIcon) closeIcon.setAttribute('data-hidden-first', '1');
    if (prevBtn) prevBtn.setAttribute('data-hidden-first', '1');
    if (nav) nav.setAttribute('data-first-step', '1');
  } else {
    if (closeIcon) closeIcon.removeAttribute('data-hidden-first');
    if (prevBtn) prevBtn.removeAttribute('data-hidden-first');
    if (nav) nav.removeAttribute('data-first-step');
  }

  // FIX: guard against driver overwriting our nav — hide immediately on any DOM change,
  // then re-apply labels/layout and show back.
  const ensureStable = () => {
    // Usuń ukrywanie, od razu aplikuj labels
    forceLabels(popover, opts);
    popover.setAttribute('data-nav-ready', '1');
  };

  // Observe whole popover (not only nav) to catch step switches
  const rootExisting = (popover as any).__driverRootObserver__ as MutationObserver | undefined;
  if (rootExisting) rootExisting.disconnect();
  const rootObs = new MutationObserver(ensureStable);
  rootObs.observe(popover, { childList: true, subtree: true });
  (popover as any).__driverRootObserver__ = rootObs;

  // Also keep nav observer (if driver tweaks only inside the nav)
  if (nav) {
    const existing = (nav as any).__driverNavObserver__ as MutationObserver | undefined;
    if (existing) existing.disconnect();
    const obs = new MutationObserver(ensureStable);
    obs.observe(nav, { childList: true, subtree: true, characterData: true });
    (nav as any).__driverNavObserver__ = obs;
  }

  // FINALLY: reveal when stable for this highlight
  popover.setAttribute('data-nav-ready', '1');
}

/** Force labels again (used by observers) */
function forceLabels(popover: HTMLElement | null, opts?: UseDriverTourOptions) {
  if (!popover) return;
  // const idx0 = getCurrentStepIndex(popover);
  const { cur, total } = parseProgress(popover);
  const idx0 = Math.max(0, cur - 1);
  const nextBtn = popover.querySelector('.driver-popover-next-btn') as HTMLButtonElement | null;
  const prevBtn = popover.querySelector('.driver-popover-prev-btn') as HTMLButtonElement | null;
  if (nextBtn) {
    const first = idx0 === 0;
    const last = idx0 === total - 1;
    const label = first
      ? opts?.firstStepNextText || opts?.nextBtnText
      : last
      ? opts?.finishBtnText || opts?.nextBtnText
      : opts?.nextBtnText;

    if (label) {
      nextBtn.textContent = label;
      nextBtn.setAttribute('aria-label', label);
      nextBtn.setAttribute('title', label);
    }
  }
  if (prevBtn && opts?.prevBtnText) {
    prevBtn.textContent = opts.prevBtnText;
    prevBtn.setAttribute('aria-label', opts.prevBtnText);
    prevBtn.setAttribute('title', opts.prevBtnText);
  }
}

export function useDriverTour(steps: DriverStep[], opts?: UseDriverTourOptions) {
  const instanceRef = useRef<DriverInstance | null>(null);
  const readyRef = useRef(false);
  const pendingStartRef = useRef(false);

  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const optsRef = useRef(opts);
  // FIX: keep opts fresh so i18n labels always up to date
  optsRef.current = opts;

  const mapSteps = useCallback((list: DriverStep[]) => {
    const o = optsRef.current;
    return list.map(s => ({
      element: s.element,
      popover: {
        title: s.popover.title ?? '',
        description: s.popover.description ?? '',
        side: s.popover.side,
        align: s.popover.align ?? 'center',
      },
      padding: s.padding ?? o?.stagePadding ?? 8,
    }));
  }, []);

  useEffect(() => {
    let mounted = true;
    const init = async () => {
      const o = optsRef.current;

      const baseOptions: any = {
        overlayOpacity: o?.overlayOpacity ?? 0.5,
        allowClose: o?.allowClose ?? true,
        showProgress: o?.showProgress ?? true,
        showButtons: true,
        animate: false,
        nextBtnText: o?.nextBtnText,
        prevBtnText: o?.prevBtnText,
        steps: mapSteps(stepsRef.current),

        onHighlighted: () => {
          // Bezpośrednio bez requestAnimationFrame opóźnienia
          const popover = document.querySelector('.driver-popover') as HTMLElement | null;
          applyNavState(popover, optsRef.current);

          // Robust NEXT handler
          const nextBtn = popover?.querySelector('.driver-popover-next-btn') as HTMLButtonElement | null;
          if (nextBtn) {
            nextBtn.removeEventListener('click', (nextBtn as any).__driverNextHandler__);
            const handler = (ev: Event) => {
              ev.preventDefault();
              ev.stopPropagation();
              const inst = instanceRef.current;
              try {
                if (typeof inst?.moveNext === 'function') inst.moveNext();
                else if (typeof inst?.drive === 'function') inst.drive();
              } catch {}
            };
            (nextBtn as any).__driverNextHandler__ = handler;
            nextBtn.addEventListener('click', handler);
          }
        },

        onDestroyed: () => {
          try {
            o?.onDestroyed?.();
          } catch {}
        },
        onCompleted: () => {
          try {
            o?.onCompleted?.();
          } catch {}
        },
        onCloseClick: () => {
          try {
            o?.onDestroyed?.();
          } catch {}
        },
      };

      const inst = await createDriver(baseOptions);
      if (!mounted) return;
      instanceRef.current = inst;
      readyRef.current = !!inst;

      if (pendingStartRef.current) {
        pendingStartRef.current = false;
        void doStart();
      }
    };
    void init();

    return () => {
      mounted = false;
      try {
        instanceRef.current?.destroy?.();
      } catch {}
      instanceRef.current = null;
      readyRef.current = false;
      pendingStartRef.current = false;

      // Cleanup observers
      const popover = document.querySelector('.driver-popover') as any;
      if (popover?.__driverRootObserver__) {
        popover.__driverRootObserver__.disconnect?.();
        delete popover.__driverRootObserver__;
      }
      const nav = document.querySelector('.driver-popover .driver-popover-navigation-btns') as any;
      if (nav?.__driverNavObserver__) {
        nav.__driverNavObserver__.disconnect?.();
        delete nav.__driverNavObserver__;
      }
    };
  }, [mapSteps]);

  // Update steps when selectors list changes
  useEffect(() => {
    const inst = instanceRef.current;
    if (!readyRef.current || !inst) return;
    const mapped = mapSteps(stepsRef.current);
    try {
      if (typeof inst.setSteps === 'function') inst.setSteps(mapped);
      else if (typeof inst.defineSteps === 'function') inst.defineSteps(mapped);
    } catch {}
  }, [mapSteps, useMemo(() => steps.map(s => s.element).join('|'), [steps])]);

  const doStart = useCallback(async () => {
    const inst = instanceRef.current;
    const o = optsRef.current;
    const list = stepsRef.current;
    if (!inst) return;

    const selectors = list.map(s => s.element);
    if (o?.waitForElementsMs) await waitUpTo(selectors, o.waitForElementsMs);

    const available = list.filter(s => exists(s.element));
    if (available.length === 0) return;

    const mapped = mapSteps(available);
    try {
      if (typeof inst.setSteps === 'function') inst.setSteps(mapped);
      else if (typeof inst.defineSteps === 'function') inst.defineSteps(mapped);
    } catch {}

    requestAnimationFrame(() => {
      if (typeof inst.drive === 'function') inst.drive();
      else if (typeof inst.start === 'function') inst.start();
    });
  }, [mapSteps]);

  const start = useCallback(() => {
    if (!readyRef.current) {
      pendingStartRef.current = true;
      return;
    }
    void doStart();
  }, [doStart]);

  const stop = useCallback(() => {
    const inst = instanceRef.current;
    if (!inst) return;
    try {
      if (typeof inst.destroy === 'function') inst.destroy();
      else if (typeof inst.reset === 'function') inst.reset();
    } catch {}
  }, []);

  // Re-apply labels when i18n texts change
  const optsSignature = useMemo(
    () =>
      JSON.stringify({
        n: opts?.nextBtnText || '',
        f: opts?.firstStepNextText || '',
        p: opts?.prevBtnText || '',
      }),
    [opts?.nextBtnText, opts?.firstStepNextText, opts?.prevBtnText],
  );

  useEffect(() => {
    if (!readyRef.current) return;
    const popover = document.querySelector('.driver-popover') as HTMLElement | null;
    if (popover) applyNavState(popover, optsRef.current);
  }, [optsSignature]);

  return { start, stop };
}
